<template>
    <div
        v-if="mostrar"
        class="modal-overlay"
        @click.self="fechar"
    >
        <div class="modal-content">
            <div class="header">
                <template v-if="icone === 'piggy-bank'">
                    <svg-icon
                        type="mdi"
                        :path="mdiPiggyBankOutline"
                        :size="60"
                        class="icon"
                    ></svg-icon>
                </template>
                <template v-else>
                    <component
                        v-if="isComponentIcon"
                        :is="iconeComponente"
                        :size="50"
                        class="icon"
                    />
                    <i v-else class="material-icons icon">
                        {{ iconeTexto }}
                    </i>
                </template>
            </div>

            <p class="modal-message">{{ mensagem }}</p>

            <div class="modal-buttons">
                <Botao
                    tipo="btn-cancel"
                    :rotulo="tituloBotaoCancelar || t('dashboard.receipt.btn_cancel')"
                    @click="cancelar"
                />
                <Botao
                    tipo="btn-confirm"
                    :rotulo="tituloBotao"
                    @click="confirmar"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import { defineComponent, computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useEmpresaStore } from '@/stores/empresa'
    import Botao from '@/components/formulario/campos/Botao.vue'
    import AccountOutline from 'vue-material-design-icons/AccountOutline.vue'
    import ThumbUp from 'vue-material-design-icons/ThumbUp.vue'
    import DeleteForever from 'vue-material-design-icons/DeleteForever.vue'
    import ContentCopy from 'vue-material-design-icons/ContentCopy.vue'
    import AccountKey from 'vue-material-design-icons/AccountKey.vue'
    import ExitToApp from 'vue-material-design-icons/ExitToApp.vue'
    import SvgIcon from '@jamescoyle/vue-icon'
    import { mdiPiggyBankOutline } from '@mdi/js'

    export default defineComponent({
        name: 'ModalConfirmacao',

        // Registro dos componentes utilizados no modal
        components: {
            Botao,
            AccountOutline,
            DeleteForever,
            ContentCopy,
            AccountKey,
            ExitToApp,
            ThumbUp,
            SvgIcon,
        },

        // Props recebidas pelo componente
        props: {
            // Controla a visibilidade do modal
            mostrar: {
                type: Boolean,
                required: true,
            },
            // Mensagem principal exibida no modal
            mensagem: {
                type: String,
                required: true,
            },
            // Texto do botão de confirmação
            tituloBotao: {
                type: String,
                required: true,
            },
            // Tipo de ícone a ser exibido no cabeçalho
            icone: {
                type: String,
                required: true,
            },
            tituloBotaoCancelar: {
                type: String,
                default: '', // Deixe o padrão como string vazia
            },
        },

        // Eventos emitidos pelo modal
        emits: ['confirmar', 'cancelar', 'fechar'],

        setup(props, { emit }) {
            const { t } = useI18n()
            const empresaStore = useEmpresaStore()

            // Obtém a cor da empresa atual para estilização
            const corEmpresa = computed(() => empresaStore.empresaAtual?.company_color || '')

            // Verifica se o ícone é um componente específico ou um ícone de texto
            const isComponentIcon = computed(() => {
                return [
                    'userStatus',
                    'receiptCancel',
                    'receiptCopy',
                    'userAccess',
                    'exit',
                    'logoutIcon',
                    'ThumbUp',
                ].includes(props.icone)
            })

            const iconeTexto = computed(() => {
                switch (props.icone) {
                    case 'meetingRoom':
                        return 'meeting_room'
                    // Adicione outros casos conforme necessário
                    default:
                        return 'info' // Ícone padrão
                }
            })
            // Define qual componente de ícone será renderizado baseado na prop icone
            const iconeComponente = computed(() => {
                switch (props.icone) {
                    case 'userStatus':
                        return 'AccountOutline'
                    case 'receiptCancel':
                        return 'DeleteForever'
                    case 'receiptCopy':
                        return 'ContentCopy'
                    case 'userAccess':
                        return 'AccountKey'
                    case 'exit':
                        return 'ExitToApp'
                    case 'ThumbUp':
                        return 'ThumbUp'
                    default:
                        return 'AccountOutline'
                }
            })

            return {
                iconeComponente,
                isComponentIcon,
                iconeTexto,
                corEmpresa,
                mdiPiggyBankOutline,
                // Métodos para emitir eventos de confirmação e cancelamento
                confirmar: () => emit('confirmar'),
                cancelar: () => emit('cancelar'),
                fechar: () => emit('fechar'),
                t,
            }
        },
    })
</script>

<style lang="scss" scoped>
    
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        padding: 0 1.5rem;
    }

    .modal-content {
        background-color: white;
        padding: 2.5rem 3rem;
        border-radius: 1.438rem;
        border: 1px solid rgb(202, 202, 202);
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        min-width: 30rem; 
        
        // Mobile
        @media (max-width: 799px) {
            width: 100%; 
        max-width: 350px;
            min-width: auto;
            padding: 2rem 1.5rem;
            border-radius: 1rem;
        }
    }

    .header {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 1rem;

        .icon {
            font-size: 3.75rem;
            color: var(--colorOpacity60);

            :deep(svg) {
                width: 4rem;
                height: 3.75rem;
            }
        }
    }

    .modal-message {
        text-align: center;
        margin-bottom: 1.5rem;
        color: #464646;
        font-size: 1.375rem;
        font-weight: 600;
        
        // Desktop
        max-width: 25rem;
        min-width: 20rem;

        // Mobile
        @media (max-width: 799px) {
            min-width: auto;
            width: 100%;
            font-size: 1.1rem;
            line-height: 1.4;
        }
    }

    .modal-buttons {
        display: flex;
        justify-content: center;
        gap: 2rem;
        align-items: center;
        width: 100%;

        @media (max-width: 799px) {
            gap: 1rem;
            flex-direction: row;
        }

        :deep(.btn) {
            width: 9.375rem;
            height: 3rem;
            font-weight: bold;
            text-transform: uppercase;
            border-radius: 8px;




            &.btn-cancel {
                background-color: white;
                color: #464646;
                border: 1px solid #464646;

                &:hover {
                color: white;
                background-color: #db0000;
                border: none;
            }
        }
            
        }
    }
</style>